/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { EventTags, IDetailMap, IEventMap } from 'apmui/EventTags';
import CustomEvent from 'apmui/util/CustomEvent';

type Handler<T, ET = Event> = (event: ET, data: T) => void;

type IHandlerOptions =
    | {
          /**
           * Register event handler as default
           */
          default: true;
          /**
           * Add event handler to the end of the chain.
           * Requires default to be set.
           */
          last: true;
      }
    | {
          default?: boolean;
          last?: false;
      };

interface IHandlerNode<Context> {
    tag: EventTags;
    handler: Handler<Context>;
    options: IHandlerOptions;
}

const defaultOptions: IHandlerOptions = {
    default: false,
    last: false,
};

class ControllerChain<Context> {
    /**
     * Default action to be taken at the end of controller chain.
     */
    public defaultController: Handler<Context>;
    protected data: Context;
    private nodes: IHandlerNode<Context>[] = [];

    constructor(data: Context) {
        this.data = data;
    }

    /**
     * Create new Event from tag and dispatch it after completing all synchronous operations.
     * @param tag    name of the event to dispatch
     * @param detail additional data object for the event
     */
    dispatch<T extends keyof IDetailMap>(tag: T, detail: IDetailMap[T]): void;
    /**
     * Create new Event from tag and dispatch it after completing all synchronous operations.
     * @param tag    name of the event to dispatch
     */
    dispatch<T extends Exclude<EventTags, keyof IDetailMap>>(tag: T): void;
    /**
     * Send event through controller chain after completing all current operations.
     * @param event  the event to dispatch
     */
    dispatch<T extends Event>(evt: T): void;
    /* implementation */
    dispatch<T>(evt: Event | EventTags, detail?: T) {
        if (typeof evt === 'string') {
            evt = new CustomEvent(evt, { detail });
        }
        Promise.resolve(evt)
            .then((event: Event) => {
                for (const node of this.nodes) {
                    if (this.testEvent(event, node)) {
                        try {
                            node.handler(event, this.data);
                        } catch (err) {
                            /* do NOT interrupt event handling */
                            console.error(err);
                        }
                    }
                }
                if (this.defaultController) {
                    this.defaultController(event, this.data);
                }
                return event;
            })
            .catch((err) => {
                /* TODO: handle chain interruption */
                console.error(err);
            });
    }

    /**
     * Subscribe to a specific APM UI event.
     * List of all public events could be accessed via apmui/EventTags module.
     *
     * New handler will be added into the beginning of controller chain.
     * @param tag     name of the event to handle, EventTags.ALL to subscribe to all events
     * @param handler event handler function
     */
    subscribe<K extends keyof IEventMap>(tag: K, handler: Handler<Context, IEventMap[K]>): void;

    /**
     * @internal
     * Subscribe to a specific APM UI event.
     * List of all public events could be accessed via apmui/EventTags module.
     *
     * New handler will be added into the beginning of controller chain.
     * @param tag     name of the event to handle
     * @param handler event handler function
     * @param options options for event
     */
    subscribe<K extends keyof IEventMap>(
        tag: K,
        handler: Handler<Context, IEventMap[K]>,
        options: IHandlerOptions
    ): void;

    /* implementation */
    subscribe<K extends keyof IEventMap>(tag: K, handler: Handler<Context, IEventMap[K]>, options?: IHandlerOptions) {
        const node = {
            tag,
            handler,
            options: options || defaultOptions,
        };

        if (node.options.default && node.options.last) {
            this.nodes.push(node);
        } else {
            this.nodes.unshift(node);
        }
    }

    /**
     * Subscribe to a specific APM UI event as a default handler.
     *
     * New handler will be added into the beginning of controller chain.
     * @param tag     name of event to handle
     * @param handler event handler function
     */
    subscribeDefault<K extends keyof IEventMap>(tag: K, handler: Handler<Context, IEventMap[K]>) {
        this.subscribe(tag, handler, { default: true });
    }

    private testEvent(event: Event, node: IHandlerNode<Context>) {
        if (node.tag !== EventTags.ALL && event.type !== node.tag) {
            return false;
        }

        if (node.options.default ? event.defaultPrevented : event.cancelBubble) {
            return false;
        }

        return true;
    }
}

export { ControllerChain, Handler };
